import { usePlayerActions } from "@utils/player/core/usePlayer";
import type { Song } from "@libs/wire";
import { useMe } from "@hooks/trpc/useMe";
import { useCallback, useMemo } from "react";
import { Platform } from "react-native";

import { useSharedSongLink } from "@hooks/useSharedSongLink";

const usePlaylistScreenData = (
  componentName: string,
  playlist?: {
    blurhash?: string | null;
    id?: string;
    images: string[] | null;
    songs: Song[];
    title: string;
    user: {
      id: string;
    };
  },
  songId?: string,
) => {
  const playerActions = usePlayerActions();

  const {
    query: { data: user },
  } = useMe();
  const songs = useMemo(() => {
    const playlistSongs = playlist?.songs ?? [];

    const availableSongs: typeof playlistSongs = [];
    const unavailableSongs: typeof playlistSongs = [];

    for (const song of playlistSongs) {
      if (song.status === "external") {
        unavailableSongs.push(song);
      } else {
        availableSongs.push(song);
      }
    }

    return [...availableSongs, ...unavailableSongs];
  }, [playlist]);
  const isOwner = playlist?.user.id === user?.id;

  const images = playlist?.images
    ? playlist.images
    : songs
        .slice(0, 4)
        .map((s) => s.albums.at(0)?.image)
        .filter(Boolean);

  const playFromCollection = playerActions.playFromCollection;

  const playSongOnLink = useCallback(
    (sharedSongId: string) => {
      if (!playlist) return;

      playFromCollection({
        songId: sharedSongId,
        collectionType: "playlist",
        collectionId: playlist.id ?? "",
        collectionName: playlist.title,
        songs,
        options: Platform.OS === "web" ? { autoPlay: false } : undefined,
      });
    },
    [playFromCollection, playlist, songs],
  );

  useSharedSongLink({ onPlay: playSongOnLink, songId, songs });

  return {
    songs,
    isOwner,
    images,
  };
};

export default usePlaylistScreenData;
